'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { motion, useReducedMotion } from 'framer-motion';
import { sendGAEvent } from '@next/third-parties/google';
import CountUpLabel from '@/components/ui/CountUpLabel';
import ArrowLabel from '@/components/ui/ArrowLabel';
import WalletIcon from '@/components/ui/icons/WalletIcon';
import ShieldIcon from '@/components/ui/icons/ShieldIcon';
import SmartphoneIcon from '@/components/ui/icons/SmartphoneIcon';

// Scroll-scrubbed frame sequence (bathroom assembling). Frames live in /public.
// Two responsive sets: landscape for desktop, a separate portrait set for
// mobile/tablet — chosen by viewport below (see MOBILE_QUERY). The two sets have
// different lengths, so the active count is picked per viewport (see `total`).
const DESKTOP_TOTAL = 100;
const MOBILE_TOTAL = 135;
const DESKTOP_BASE = '/assets/media/Hero/frames-desktop';
const MOBILE_BASE = '/assets/media/Hero/frames-mobile';
const MOBILE_QUERY = '(max-width: 1023px)'; // mobile + tablet → mobile frames
const DESKTOP_QUERY = '(min-width: 1024px)';
// The poster IS the animation's first frame (frame001) — identical image and the
// same cover-framing the canvas draws at draw(0), so the poster→canvas hand-off is
// seamless (no flash). It's rendered as a real full-viewport <img> below (the LCP
// element) and preloaded per device. Cheaper than a separate poster and already
// downloaded for the animation.
const MOBILE_POSTER = `${MOBILE_BASE}/frame001.webp`;
const DESKTOP_POSTER = `${DESKTOP_BASE}/frame001.webp`;
const framePath = (base: string, i: number) =>
  `${base}/frame${String(i).padStart(3, '0')}.webp`;

const AUTO_FPS = 19; // ambient auto-play speed when idle, frames/sec (lower = slower)
const IDLE_MS = 140; // how long after a scroll before ambient auto-play resumes
const LERP_TAU = 0.12; // how snappily the frame follows the scroll while scrolling
const FADE_END = 0.18; // hero copy fully faded by this much scroll progress

const BOOT_FRAMES = 12; // contiguous frames needed before the canvas loop starts
const FRAME_CONCURRENCY = 6; // parallel frame downloads (full flood starves the critical path)
const LOAD_FALLBACK_MS = 2500; // start frames this long after hydration even if `load` hasn't fired

export default function HeroSection() {
  const t = useTranslations('hero');
  const reduceMotion = useReducedMotion();

  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<HTMLImageElement[]>([]);
  // Highest frame index (1-based) up to which EVERY frame has loaded — the draw
  // loop never asks for a frame beyond this, so a half-loaded set still animates.
  const loadedRef = useRef(0);

  const [ready, setReady] = useState(false); // enough frames in — start the loop
  // null until measured on the client; picks the desktop vs mobile/tablet set.
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  // Frame count for the active set (the mobile sequence is shorter). Defaults to
  // the desktop count until the viewport is measured; only used once a set loads.
  const total = isMobile ? MOBILE_TOTAL : DESKTOP_TOTAL;

  const badges = [
    { label: t('badge1Label'), desc: t('badge1Desc'), icon: WalletIcon },
    { label: t('badge2Label'), desc: t('badge2Desc'), icon: ShieldIcon },
    { label: t('badge3Label'), desc: t('badge3Desc'), icon: SmartphoneIcon },
  ];

  // Pick the desktop vs mobile/tablet frame set, and re-pick if the viewport
  // crosses the breakpoint (e.g. tablet rotation).
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Load the active frame set WITHOUT competing with the critical path: the SSR
  // poster + hero copy are the LCP, so the frame flood waits for the `load` event
  // (with a timeout fallback), then trickles in FRAME_CONCURRENCY-at-a-time in
  // index order. The canvas loop starts at BOOT_FRAMES and the rest top up in the
  // background — the draw loop clamps to loadedRef so gaps never flash.
  // Re-runs when isMobile flips (loads the other set).
  useEffect(() => {
    if (isMobile === null) return; // wait until the viewport is measured
    const base = isMobile ? MOBILE_BASE : DESKTOP_BASE;
    let cancelled = false;
    let started = false;
    let next = 1; // next frame index to request
    let inFlight = 0;
    let count = 0;
    let contiguous = 0;
    const loaded: boolean[] = new Array(total + 1).fill(false);
    const imgs: HTMLImageElement[] = [];
    setReady(false);
    loadedRef.current = 0;

    const pump = () => {
      while (!cancelled && inFlight < FRAME_CONCURRENCY && next <= total) {
        const i = next++;
        inFlight += 1;
        const img = new Image();
        const done = () => {
          if (cancelled) return;
          inFlight -= 1;
          count += 1;
          loaded[i] = true;
          while (loaded[contiguous + 1]) contiguous += 1;
          loadedRef.current = contiguous;
          if (contiguous >= Math.min(BOOT_FRAMES, total)) setReady(true);
          pump();
        };
        img.onload = done;
        img.onerror = done; // a missing frame shouldn't stall the sequence
        img.src = framePath(base, i);
        imgs[i - 1] = img;
      }
    };
    imagesRef.current = imgs;

    const startFrames = () => {
      if (cancelled || started) return;
      started = true;
      pump();
    };

    // Wait for the page to finish loading (or the fallback timer) so fonts, CSS
    // and JS never queue behind a hundred webp requests on slow connections.
    const fallback = window.setTimeout(startFrames, LOAD_FALLBACK_MS);
    if (document.readyState === 'complete') {
      startFrames();
    } else {
      window.addEventListener('load', startFrames, { once: true });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(fallback);
      window.removeEventListener('load', startFrames);
    };
  }, [isMobile, total]);

  // Hybrid driver: scroll position scrubs the frames (you stay pinned through the
  // tall section and only reach the next section at the last frame). When you stop
  // scrolling, the sequence keeps drifting very slowly (ping-pong) so it stays live.
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !section || !ctx) return;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const sizeCanvas = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
    };

    let drawn = -1;
    const draw = (index: number) => {
      const img = imagesRef.current[index];
      if (!img || !img.complete || !img.naturalWidth) return;
      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const scale = Math.max(cw / iw, ch / ih); // cover — always fills the viewport
      const dw = iw * scale;
      const dh = ih * scale;
      ctx.fillStyle = '#1a2421';
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      drawn = index;
    };

    sizeCanvas();

    // Reduced motion: no animation — just show the finished bathroom, static.
    // Until the background loader delivers the last frame, show the newest one
    // it has (a light poll — no rAF loop needed for a static image).
    if (reduceMotion) {
      const lastLoaded = () => Math.min(total - 1, Math.max(0, loadedRef.current - 1));
      draw(lastLoaded());
      const poll = window.setInterval(() => {
        if (drawn !== lastLoaded()) draw(lastLoaded());
        if (lastLoaded() === total - 1) window.clearInterval(poll);
      }, 300);
      const onResize = () => {
        sizeCanvas();
        draw(lastLoaded());
      };
      window.addEventListener('resize', onResize);
      return () => {
        window.clearInterval(poll);
        window.removeEventListener('resize', onResize);
      };
    }

    const scrollProgress = () => {
      const scrollable = section.offsetHeight - window.innerHeight;
      const p = scrollable > 0 ? (window.scrollY - section.offsetTop) / scrollable : 0;
      return Math.max(0, Math.min(1, p));
    };

    let pos = 0; // float frame position
    let dir = 1; // ambient ping-pong direction
    let anchorP = 0; // scroll progress captured when scrolling begins
    let anchorPos = 0; // frame captured when scrolling begins (for continuity)
    let wasScrolling = false;
    let lastScroll = -Infinity; // timestamp of last scroll
    let lastT = 0;
    let raf = 0;
    let running = false;

    const tick = (now: number) => {
      if (!lastT) lastT = now;
      const dt = Math.min(0.05, (now - lastT) / 1000);
      lastT = now;

      const p = scrollProgress();
      if (now - lastScroll < IDLE_MS) {
        // Scrolling → scrub, anchored so the frame continues from where the
        // auto-play left off (no jump back to 0) and reaches the last frame as
        // the sticky section ends. You stay pinned until then.
        if (!wasScrolling) {
          wasScrolling = true;
          anchorP = p;
          anchorPos = pos;
        }
        const target =
          p >= anchorP
            ? anchorPos +
              ((p - anchorP) / Math.max(1e-4, 1 - anchorP)) * (total - 1 - anchorPos)
            : anchorPos * (p / Math.max(1e-4, anchorP));
        pos += (target - pos) * (1 - Math.exp(-dt / LERP_TAU));
      } else {
        // Idle → auto-play the full sequence (ping-pong) from the current frame.
        wasScrolling = false;
        pos += AUTO_FPS * dir * dt;
        if (pos >= total - 1) {
          pos = total - 1;
          dir = -1;
        } else if (pos <= 0) {
          pos = 0;
          dir = 1;
        }
      }

      // Never ask for a frame the background loader hasn't delivered yet — hold
      // the newest loaded one instead (the sequence catches up as frames land).
      const maxIdx = Math.min(total - 1, Math.max(0, loadedRef.current - 1));
      const idx = Math.max(0, Math.min(maxIdx, Math.round(pos)));
      if (idx !== drawn) draw(idx);

      const overlay = overlayRef.current;
      if (overlay) {
        const o = 1 - Math.min(1, p / FADE_END);
        overlay.style.opacity = String(o);
        overlay.style.pointerEvents = o < 0.05 ? 'none' : 'auto';
      }

      raf = requestAnimationFrame(tick);
    };

    const startLoop = () => {
      if (running) return;
      running = true;
      lastT = 0;
      raf = requestAnimationFrame(tick);
    };
    const stopLoop = () => {
      if (!running) return;
      running = false;
      cancelAnimationFrame(raf);
    };

    const onScroll = () => {
      lastScroll = performance.now();
    };
    const onResize = () => {
      sizeCanvas();
      draw(Math.round(pos));
    };

    // Only animate while the hero is on screen (saves CPU/battery elsewhere).
    let io: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? startLoop() : stopLoop()),
        { threshold: 0 }
      );
      io.observe(section);
    } else {
      startLoop();
    }

    draw(0);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      stopLoop();
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [ready, reduceMotion, total]);

  // The hero copy panel animates in with a CSS `hero-rise` class (fade + rise) —
  // see globals.css. It's CSS, not framer-motion, so it plays from first paint
  // (not after hydration) and keeps the copy an early LCP candidate on mobile.

  return (
    <>
      {/* Start the poster download during HTML parse — before hydration kicks off
          the frame flood — so the loader backdrop is visible near-instantly. The
          media attribute makes each device fetch only its own poster. */}
      <link rel="preload" as="image" href={MOBILE_POSTER} media={MOBILE_QUERY} fetchPriority="high" />
      <link rel="preload" as="image" href={DESKTOP_POSTER} media={DESKTOP_QUERY} fetchPriority="high" />

      {/* Tall sticky hero: you stay pinned while the frames scrub with scroll, and
          only reach the section below once the animation hits its last frame. When
          idle, the sequence keeps drifting very slowly so it always looks live. */}
      <section
        id="hero"
        ref={sectionRef}
        className={reduceMotion ? 'relative h-[100dvh]' : 'relative h-[300vh]'}
      >
        {/* 100dvh (dynamic viewport) so the pinned hero fills the *visible* area
            on iOS even while the browser toolbar is showing — content centers in
            view on first paint instead of dropping behind the toolbar. */}
        <div className="sticky top-0 h-[100dvh] overflow-hidden bg-surface">
          {/* LCP element: a real, full-viewport poster painted from the SSR HTML
              (no ready/isMobile gate, no opacity/blur/transform). <picture> picks
              the per-device first frame at parse time using the SAME media queries
              as the <link rel="preload"> above, so the preloaded bytes are reused
              and exactly one poster is fetched. The <canvas> (next sibling, stacked
              above by DOM order) draws the identical frame001 once ready → seamless
              hand-off, no flash. */}
          <picture>
            <source media={MOBILE_QUERY} srcSet={MOBILE_POSTER} />
            <source media={DESKTOP_QUERY} srcSet={DESKTOP_POSTER} />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={DESKTOP_POSTER}
              alt=""
              aria-hidden
              fetchPriority="high"
              decoding="async"
              loading="eager"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </picture>
          <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />

          {/* Scrim disabled — frames render at full brightness (no tint). Re-add
              opacity (e.g. from-surface/15 to-surface/25) if copy legibility suffers. */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-surface/0 via-surface/0 to-surface/0" />

          {/* Hero copy — fades out as you scroll into the animation. pt reserves
              the fixed-header band (~64px mobile / ~88px desktop) so the tall
              ru/ua copy never slides beneath the header; pb reserves a bottom band
              (+ iOS safe area) because in-app browsers (Telegram, Instagram…) draw
              their bottom toolbar OVER the page. The panel centers via my-auto —
              NOT justify-center — so when the copy is taller than the space
              between the two bands it pins below the header and overflows
              downward, instead of spilling under the header. */}
          <div
            ref={overlayRef}
            className="absolute inset-0 flex flex-col px-margin-mobile pt-20 pb-[calc(4rem_+_env(safe-area-inset-bottom))] short:pt-[4.5rem] lg:px-margin-desktop lg:pt-24 lg:pb-24"
          >
            <div className="mx-auto my-auto w-full max-w-6xl">
              <div className="hero-rise max-w-4xl rounded-3xl border border-outline-variant/20 bg-surface/50 px-6 py-8 backdrop-blur-sm short:py-5 sm:px-9 sm:py-10 lg:px-10">
                <h1 className="whitespace-pre-line font-headline font-bold text-on-surface text-[1.5rem] leading-[1.15] tracking-[0.02em] short:text-[1.25rem] short:leading-[1.1] sm:text-headline-xl sm:leading-[1.1]">
                  {t('headline')}
                </h1>
                <p className="mt-stack-lg max-w-2xl text-body-md text-on-surface-variant short:mt-4 short:text-sm short:leading-snug md:text-body-lg">
                  {t('subheadline')}
                </p>
                <div className="mt-stack-lg flex flex-col items-start gap-stack-md short:mt-4 short:gap-3">
                  <a
                    href="#calc-form"
                    onClick={() => sendGAEvent('event', 'cta_click', { cta_location: 'hero' })}
                    className="cta-gold group inline-flex items-center justify-center rounded-full px-8 py-5 text-center font-headline text-body-md font-bold text-background transition-transform duration-200 short:px-6 short:py-3 hover:scale-[1.03] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <ArrowLabel>{t('cta')}</ArrowLabel>
                  </a>
                </div>
                <span className="mt-stack-md block text-label-caps uppercase tracking-widest text-on-surface-variant short:mt-3">
                  {t('ctaMicro')}
                </span>
              </div>
            </div>
            {!reduceMotion && (
              <span
                aria-hidden
                className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce text-lg text-on-surface/70"
              >
                ↓
              </span>
            )}
          </div>

          {/* No loading overlay: the SSR poster (frame001) + hero copy ARE the
              first paint on every viewport — the frame set tops up silently in
              the background and the canvas takes over from the identical frame. */}
        </div>
      </section>

      {/* Benefit badges — just below the hero. Each card materializes on a
          stagger (blur + rise + scale), and its leading number (0, 3, 100%)
          counts up the first time it's seen. */}
      <section className="relative border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:px-margin-desktop">
        <motion.ul
          className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-6 sm:grid-cols-3"
          initial={reduceMotion ? false : 'hidden'}
          whileInView="show"
          viewport={{ once: true, margin: '-10% 0px' }}
          variants={{ show: { transition: { staggerChildren: 0.14, delayChildren: 0.08 } } }}
        >
          {badges.map(({ label, desc, icon: Icon }) => (
            <motion.li
              key={label}
              variants={{
                hidden: { opacity: 0, y: 40, scale: 0.94, filter: 'blur(10px)' },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  filter: 'blur(0px)',
                  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                },
              }}
              whileHover={reduceMotion ? undefined : { y: -6 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className="group forest-card p-6 transition-colors hover:border-primary/40"
            >
              <div className="mb-stack-md text-primary transition-colors group-hover:text-primary-fixed">
                <Icon />
              </div>
              <p className="font-headline text-headline-lg-mobile font-bold tracking-[0.02em] text-on-surface">
                <CountUpLabel label={label} />
              </p>
              <p className="mt-stack-sm text-body-md text-on-surface-variant">{desc}</p>
            </motion.li>
          ))}
        </motion.ul>
      </section>
    </>
  );
}
